import { Entity } from '../../../shared/models/Entity';
import { Email } from '../value-objects/Email';
import { Phone } from '../value-objects/Phone';
import { UserId } from '../value-objects/UserId';
import { UserRole } from '../value-objects/UserRole';
import { Role } from '../value-objects/Role';
import { LoginAttempt } from '../value-objects/LoginAttempt';

/**
 * 사용자 엔티티
 * 인증된 사용자 정보를 표현
 */
export class User extends Entity<UserId> {
  private _email: Email;
  private _phone: Phone;
  private _name: string;
  private _role: UserRole;
  private _isActive: boolean;
  private _lastLoginAt?: Date;
  private _profileImageUrl?: string;
  private _failedLoginAttempts: LoginAttempt[];
  private _accountLockedUntil?: Date;
  private _passwordChangedAt?: Date;
  private _organizationId?: string;
  private _organizationName?: string;

  constructor(props: {
    id: UserId;
    email: Email;
    phone: Phone;
    name: string;
    role: UserRole;
    isActive?: boolean;
    lastLoginAt?: Date;
    profileImageUrl?: string;
    failedLoginAttempts?: LoginAttempt[];
    accountLockedUntil?: Date;
    passwordChangedAt?: Date;
    organizationId?: string;
    organizationName?: string;
  }) {
    super(props.id);
    this._email = props.email;
    this._phone = props.phone;
    this._name = props.name;
    this._role = props.role;
    this._isActive = props.isActive ?? true;
    this._lastLoginAt = props.lastLoginAt;
    this._profileImageUrl = props.profileImageUrl;
    this._failedLoginAttempts = props.failedLoginAttempts || [];
    this._accountLockedUntil = props.accountLockedUntil;
    this._passwordChangedAt = props.passwordChangedAt;
    this._organizationId = props.organizationId;
    this._organizationName = props.organizationName;
  }

  // Getters
  get email(): Email {
    return this._email;
  }

  get phone(): Phone {
    return this._phone;
  }

  get name(): string {
    return this._name;
  }

  get role(): UserRole {
    return this._role;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  get lastLoginAt(): Date | undefined {
    return this._lastLoginAt;
  }

  get profileImageUrl(): string | undefined {
    return this._profileImageUrl;
  }

  get failedLoginAttempts(): readonly LoginAttempt[] {
    return [...this._failedLoginAttempts];
  }

  get accountLockedUntil(): Date | undefined {
    return this._accountLockedUntil;
  }

  get passwordChangedAt(): Date | undefined {
    return this._passwordChangedAt;
  }

  get organizationId(): string | undefined {
    return this._organizationId;
  }

  get organizationName(): string | undefined {
    return this._organizationName;
  }

  // 비즈니스 메서드

  /**
   * 사용자 활성화
   */
  activate(): void {
    if (this._isActive) {
      throw new Error('이미 활성화된 사용자입니다');
    }
    this._isActive = true;
    this.touch();
  }

  /**
   * 사용자 비활성화
   */
  deactivate(): void {
    if (!this._isActive) {
      throw new Error('이미 비활성화된 사용자입니다');
    }
    this._isActive = false;
    this.touch();
  }

  /**
   * 로그인 시간 업데이트
   */
  updateLastLogin(): void {
    this._lastLoginAt = new Date();
    this.touch();
  }

  /**
   * 프로필 정보 업데이트
   */
  updateProfile(props: {
    name?: string;
    phone?: Phone;
    profileImageUrl?: string;
  }): void {
    if (props.name !== undefined) {
      this._name = props.name;
    }
    if (props.phone !== undefined) {
      this._phone = props.phone;
    }
    if (props.profileImageUrl !== undefined) {
      this._profileImageUrl = props.profileImageUrl;
    }
    this.touch();
  }

  /**
   * 역할 변경
   */
  changeRole(newRole: UserRole): void {
    if (this._role.equals(newRole)) {
      throw new Error('동일한 역할로는 변경할 수 없습니다');
    }
    this._role = newRole;
    this.touch();
  }

  /**
   * 권한 확인
   */
  hasPermission(permission: string): boolean {
    return this._role.hasPermission(permission);
  }

  /**
   * 로그인 실패 기록
   */
  recordFailedLogin(attempt: LoginAttempt): void {
    this._failedLoginAttempts.push(attempt);
    
    // 최근 24시간 내 실패 시도만 유지
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    this._failedLoginAttempts = this._failedLoginAttempts.filter(
      attempt => attempt.timestamp > oneDayAgo
    );

    // 5회 실패 시 계정 잠금
    if (this.getRecentFailedAttempts(30) >= 5) {
      this.lockAccount(30); // 30분간 잠금
    }
    
    this.touch();
  }

  /**
   * 로그인 성공 처리
   */
  recordSuccessfulLogin(): void {
    this._lastLoginAt = new Date();
    this._failedLoginAttempts = []; // 실패 기록 초기화
    this._accountLockedUntil = undefined; // 잠금 해제
    this.touch();
  }

  /**
   * 계정 잠금
   */
  lockAccount(minutes: number): void {
    this._accountLockedUntil = new Date(Date.now() + minutes * 60 * 1000);
    this.touch();
  }

  /**
   * 계정 잠금 해제
   */
  unlockAccount(): void {
    this._accountLockedUntil = undefined;
    this._failedLoginAttempts = [];
    this.touch();
  }

  /**
   * 계정 잠금 상태 확인
   */
  isAccountLocked(): boolean {
    return this._accountLockedUntil ? new Date() < this._accountLockedUntil : false;
  }

  /**
   * 계정 잠금 상태 확인 (별칭 메서드)
   */
  isLocked(): boolean {
    return this.isAccountLocked();
  }

  /**
   * 최근 실패 시도 횟수
   */
  getRecentFailedAttempts(minutes: number): number {
    const cutoff = new Date(Date.now() - minutes * 60 * 1000);
    return this._failedLoginAttempts.filter(
      attempt => attempt.timestamp > cutoff && attempt.isFailed()
    ).length;
  }

  /**
   * 비밀번호 변경 기록
   */
  recordPasswordChange(): void {
    this._passwordChangedAt = new Date();
    this.touch();
  }

  /**
   * 비밀번호 변경 필요 여부 (90일 경과)
   */
  isPasswordChangeRequired(): boolean {
    if (!this._passwordChangedAt) return true;
    
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    return this._passwordChangedAt < ninetyDaysAgo;
  }

  /**
   * 역할별 권한 확인 (Role enum 사용)
   */
  hasRole(role: Role): boolean {
    return this._role.value === role;
  }

  /**
   * JSON 변환
   */
  toJSON(): object {
    return {
      id: this._id.value,
      email: this._email.value,
      phone: this._phone.value,
      name: this._name,
      role: this._role.value,
      isActive: this._isActive,
      lastLoginAt: this._lastLoginAt?.toISOString(),
      profileImageUrl: this._profileImageUrl,
      organizationId: this._organizationId,
      organizationName: this._organizationName,
      createdAt: this._createdAt.toISOString(),
      updatedAt: this._updatedAt.toISOString(),
    };
  }

  /**
   * 팩토리 메서드
   */
  static create(props: {
    email: string;
    phone: string;
    name: string;
    role: string;
  }): User {
    return new User({
      id: UserId.create(),
      email: Email.create(props.email),
      phone: Phone.create(props.phone),
      name: props.name,
      role: UserRole.create(props.role),
    });
  }
}